<template>
  <div>
    <RevInputSelectSearchable
      id="swap-select"
      :class="{ 'mb-8': displaySelectExample }"
      :clear-button-aria-label="i18n(translations.clearButtonAriaLabel)"
      :defaultOption="displayDefaultOption ? defaultOption : undefined"
      :hideCheckboxes="true"
      :label="label || i18n(translations.swapSelectPlaceholder)"
      listbox-placement="bottom"
      :model-value="selectModel"
      :no-options-message="i18n(translations.swapSelectNoOptionsMessage)"
      :options="filteredOptions"
      @focus="handleFocus"
      @text-change="handleTextChange"
      @update:model-value="
        (option) => handleModelValueUpdate(option as OptionItem | null)
      "
    />

    <span v-if="displaySelectExample" class="text-static-default-low body-2">
      {{ selectExampleText ?? i18n(translations.swapSelectExample) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevInputSelectSearchable } from '@ds/components/InputSelectSearchable'

import { useBuybackPopularSearches } from '../../composables/buybackSearch/useBuybackPopularSearches'
import { useBuybackSearch } from '../../composables/buybackSearch/useBuybackSearch'
import type { BuybackProduct } from '../../types'

import translations from './TradeInSearchBar.translations'

type OptionItem = {
  id: number | string
  label: string
  value: string
}

interface SearchProduct {
  label: string
  suffix: string
  id: string
  value: string
}

const {
  initialQuery = undefined,
  label = undefined,
  zone,
  selectExampleText = undefined,
  displaySelectExample = true,
  displayDefaultOption = true,
  defaultOptionLabel = '',
  tracking = undefined,
} = defineProps<{
  initialQuery?: string
  label?: string
  zone: string
  displaySelectExample?: boolean
  selectExampleText?: string
  displayDefaultOption?: boolean
  defaultOptionLabel?: string
  tracking?: Tracking
}>()

const emits = defineEmits<{
  (e: 'item-selected', value: BuybackProduct): void
  (e: 'default-item-selected'): void
}>()

const i18n = useI18n()
const { query, hits } = useBuybackSearch('standard-search', {
  debounce: true,
  debounceTime: 300,
})
const { popularSearches } = useBuybackPopularSearches()
const { trackClick, trackFormSubmit } = useTracking()

const selectModel = ref<OptionItem | null>(null)

function buybackProductsToSearchProduct(
  products: BuybackProduct[],
): SearchProduct[] {
  return products.map((product) => ({
    label: product.model,
    suffix: i18n(translations.swapSelectSuffixOption, {
      price: i18n.price(product.price, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    }),
    id: product.id,
    value: product.id,
  }))
}

const defaultOption = {
  id: 'default',
  label: defaultOptionLabel || i18n(translations.swapSelectLabelOptionDefault),
  value: 'default',
}

const filteredOptions = computed(() => {
  if (query.value === '') {
    return buybackProductsToSearchProduct(popularSearches.value)
  }

  return buybackProductsToSearchProduct(hits.value)
})

function handleTextChange(value: string) {
  if (value !== selectModel.value?.label) {
    query.value = value
  }
}

function handleModelValueUpdate(option: OptionItem | null) {
  selectModel.value = option

  // when search bar is reset -> option becomes null
  if (!option) return

  if (option.value === defaultOption.value) {
    emits('default-item-selected')

    trackFormSubmit({
      zone,
      name: option.value,
      payload: {
        search_type: 'no result',
        query: query.value,
      },
    })

    return
  }

  const selectedHit = [...hits.value, ...popularSearches.value].find(
    (hit) => hit.id === option.id,
  )
  if (selectedHit) {
    emits('item-selected', selectedHit)
    trackFormSubmit({
      zone,
      name: selectedHit?.model ?? '',
      payload: {
        product_public_id: option.id,
        search_type: query.value === '' ? 'popularSearches' : 'suggestions',
        query: query.value,
      },
    })
  }
}

function handleFocus() {
  trackClick(removeEmptyValuesInObject({ zone, name: 'yes', ...tracking }))
}

watch(
  [() => initialQuery],
  ([newInitialQuery]) => {
    if (newInitialQuery) {
      selectModel.value = {
        id: newInitialQuery,
        label: newInitialQuery,
        value: newInitialQuery,
      }
      query.value = newInitialQuery
    }
  },
  { immediate: true },
)
</script>
